import { Mutex } from "async-mutex";
import {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
  fetchBaseQuery,
} from "@reduxjs/toolkit/query/react";
import { isObject, isString } from "lodash";
import Swal from "sweetalert2";

/**
 * Auth-aware RTK Query base — ported verbatim from the existing front/ app so the
 * new app uses the same JWT + refresh-token contract against the existing NestJS API
 * without requiring any API changes.
 *
 * Per-endpoint `extraOptions.noJwt = true` bypasses the Authorization header.
 */
export class BaseQuery {
  private readonly mutex = new Mutex();

  baseQuery = fetchBaseQuery({ baseUrl: process.env.REACT_APP_API_URL });

  baseQueryWithReauth: BaseQueryFn<
    string | FetchArgs,
    unknown,
    FetchBaseQueryError,
    { noJwt?: boolean }
  > = async (args, api, extraOptions) => {
    await this.mutex.waitForUnlock();

    if (extraOptions?.noJwt) return this.baseQuery(args, api, extraOptions);

    const jwtKey = process.env.REACT_APP_JWT_NAME || "jwt";
    const jwt = localStorage.getItem(jwtKey);

    const headers = new Headers();
    if (jwt) headers.set("Authorization", `Bearer ${jwt}`);
    if (typeof args === "string") {
      args = { url: args };
    }
    args.headers = headers;
    let result = await this.baseQuery(args, api, extraOptions);

    if (result.error && result.error.status === 401) {
      if (!this.mutex.isLocked()) {
        const release = await this.mutex.acquire();
        try {
          const refreshResult = await this.baseQuery(
            {
              url: "/auth/refreshToken",
              credentials: "include",
              method: "POST",
            },
            api,
            extraOptions,
          );
          if (refreshResult.error) {
            localStorage.removeItem(jwtKey);
            await Swal.fire({
              icon: "warning",
              text: "Session Expired, please login again",
            });
            window.location.href = `${process.env.PUBLIC_URL}/login`;
          }

          if (refreshResult.data) {
            const { data } = refreshResult;
            if (isObject(data) && "jwt" in data && isString(data.jwt)) {
              localStorage.setItem(jwtKey, data.jwt);
              const newHeaders = new Headers();
              newHeaders.set("Authorization", `Bearer ${data.jwt}`);
              args.headers = newHeaders;
              result = await this.baseQuery(args, api, extraOptions);
            } else {
              Swal.fire({
                icon: "error",
                text: "Something went wrong. Please contact Fugro Staff",
              });
              localStorage.removeItem(jwtKey);
            }
          }
        } finally {
          release();
        }
      } else {
        await this.mutex.waitForUnlock();
        const retryJwt = localStorage.getItem(jwtKey);
        const retryHeaders = new Headers();
        if (retryJwt) retryHeaders.set("Authorization", `Bearer ${retryJwt}`);

        result = await this.baseQuery(
          { ...(args as FetchArgs), headers: retryHeaders },
          api,
          extraOptions,
        );
      }
    }
    return result;
  };
}

export const sharedBaseQuery = new BaseQuery();
